import { useServerSideDataModel } from "src/components/common/dataGrid/useServerSideDataModel.ts";
import { dateColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField";
import { LazyMultiSelectField } from "src/components/common/inputFields/LazyMultiSelectField.tsx";
import { CustomerOrderTypeApi } from "src/api/generated/erp/sales/basedata/customerOrderTypeApi.ts";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { useOpenLegacyView } from "src/components/views/legacy/hooks.ts";
import { dataGridCellClassNames } from "src/components/common/dataGrid/styles/dataGridClassNames.ts";
import dayjs from "dayjs";
import { ControlledAsyncDataGrid } from "src/components/common/dataGrid/ControlledAsyncDataGrid.tsx";
import { AsyncButtonContextMenuItem } from "../../../../common/contextMenu/AsyncButtonContextMenuItem.tsx";
import { faBoxOpen, faCheckCircle, faFileInvoiceDollar } from "@fortawesome/pro-regular-svg-icons";
import {
	PickableCustomerOrdersDataGridApi,
	PickableCustomerOrdersDataGridApi_PickableCustomerOrder,
} from "src/api/generated/erp/sales/picking/api/pickableCustomerOrdersDataGridApi.ts";
import { openLegacyCustomerOrderView } from "../../../legacy/legacyViewAdapters.ts";
import { useGenericDialog } from "../../../../common/dialogs/useGenericDialog.ts";
import { ReservableCustomerOrderLinesDataGrid } from "./ReservableCustomerOrderLinesDataGrid.tsx";
import { CustomerOrderPickingView } from "./CustomerOrderPickingView.tsx";

export interface PickableCustomersOrdersDataGridProps {}

export const PickableCustomersOrdersDataGrid = ({}: PickableCustomersOrdersDataGridProps) => {
	const { serverSideDataGridProps, refreshData, currentParams, onlySelectedRow } =
		useServerSideDataModel({
			gridId: "E1F87A04512667AA",
			fetchData: PickableCustomerOrdersDataGridApi.getRows,
			getRowId: (row) => row.customerOrderId,
			initialParams: {
				searchQuery: "",
				showOnlyDefaultSite: true,
				customerOrderTypeIdFilter: Array<number>(),
			},
		});

	const openLegacyView = useOpenLegacyView();

	const { openDialog } = useGenericDialog();

	return (
		<ControlledAsyncDataGrid<PickableCustomerOrdersDataGridApi_PickableCustomerOrder>
			disableMultipleRowSelection
			columns={[
				integerColumn({
					field: "customerOrderId",
					headerName: i18n.t("order_number"),
					width: 120,
				}),
				textColumn({
					field: "siteName",
					headerName: i18n.t("site"),
				}),
				textColumn({
					field: "customerName",
					headerName: i18n.t("customer"),
					width: 300,
				}),
				textColumn({
					field: "orderReference",
					headerName: i18n.t("reference"),
					width: 300,
				}),
				dateColumn({
					field: "earliestPickingDate",
					headerName: i18n.t("earliest_picking_date"),
					width: 200,
					cellClassName: (params) => {
						if (dayjs(params.row.earliestPickingDate).isBefore(dayjs(), "day")) {
							return dataGridCellClassNames.warning;
						}
						return "";
					},
				}),
			]}
			actionBarComponents={
				<>
					<AavoTextField
						placeholder={i18n.t("search")}
						onSubmit={async (search) => {
							await refreshData({ searchQuery: search });
						}}
					/>
					<LazyMultiSelectField
						label={i18n.t("order_type")}
						fetchOptions={CustomerOrderTypeApi.getAll}
						getOptionKey={(option) => option.customerOrderTypeId}
						getOptionLabel={(option) => option.name}
						value={currentParams.customerOrderTypeIdFilter}
						onChange={async (newValue) => {
							await refreshData({ customerOrderTypeIdFilter: newValue });
						}}
						sx={{
							minWidth: 160,
						}}
					/>
					<AavoButton
						label={i18n.t("picking")}
						icon={faBoxOpen}
						variant={"outlined"}
						disabled={onlySelectedRow === undefined}
						onClick={() => {
							if (onlySelectedRow === undefined) return;
							openDialog(() => ({
								title: i18n.t("picking"),
								size: "fullscreen",
								children: (
									<CustomerOrderPickingView customerOrder={onlySelectedRow} />
								),
							}));
						}}
					/>
					<AavoCheckbox
						label={i18n.t("only_default_site")}
						checked={currentParams.showOnlyDefaultSite}
						onChange={async (_, checked) => {
							await refreshData({ showOnlyDefaultSite: checked });
						}}
					/>
				</>
			}
			rowContextMenuComponents={({ row }) => [
				<AsyncButtonContextMenuItem
					key="customerOrder"
					icon={faFileInvoiceDollar}
					label={i18n.t("customer_order")}
					onClick={() => {
						openLegacyCustomerOrderView(openLegacyView, row.customerOrderId);
					}}
				/>,
				<AsyncButtonContextMenuItem
					key="customerOrderAvailabilityCheck"
					icon={faCheckCircle}
					label={i18n.t("availability_check")}
					onClick={() => {
						openDialog(() => ({
							title: i18n.t("availability_check"),
							size: "lg",
							children: (
								<ReservableCustomerOrderLinesDataGrid
									customerOrderId={row.customerOrderId}
								/>
							),
						}));
					}}
				/>,
			]}
			{...serverSideDataGridProps}
		/>
	);
};
